import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash,
  Upload,
  X,
  Tag,
  RefreshCw,
  Power,
  Barcode as BarcodeIcon,
  AlertTriangle,
  SlidersHorizontal,
  Trash2,
  ChevronFirst,
  ChevronLeft,
  ChevronRight,
  ChevronLast,
  Grid,
  List,
  Package
} from 'lucide-react';
import { supabase, withCache, clearQueryCache } from '../../lib/supabase';
import BarcodeGenerator from '../BarcodeGenerator';
import { ProxyImage } from '../../lib/imageProxy';
import Select from 'react-select';
import { useDebounce } from '../../hooks/useDebounce';

interface Category {
  id: string;
  name: string;
  image?: string;
  parent_id?: string;
  parent?: { name: string; id: string; } | null;
}

interface PriceList {
  price_type: 'contado' | 'credito' | 'mayorista' | 'publico';
  min_quantity: number;
  price: number;
  offer_price?: number | null;
  offer_days?: number | null;
  has_offer?: boolean;
}

interface ProductImage {
  id?: string;
  image_url: string;
  order: number;
}

interface ProductSpecification {
  name: string;
  value: string;
  order: number;
}

interface Product {
  id: string;
  name: string;
  description: string;
  base_price: number; // Mantenido por compatibilidad
  main_image: string;
  additional_images?: ProductImage[];
  specifications?: ProductSpecification[];
  stock: number;
  min_order: number;
  status: 'active' | 'inactive' | 'out_of_stock';
  // IDs de las categorías asociadas (N:M)
  category_ids?: string[];
  // Objetos de categorías asociadas (N:M)
  categories?: Category[];
  prices?: PriceList[];
  created_at: string;
  code?: string;
  barcode?: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  slug?: string;
  show_in_ecommerce?: boolean;
  company?: string[];
  sale_price?: number;
  sale_start_date?: string;
  sale_end_date?: string;
  has_offer?: boolean;
}

// Definir withRetry directamente en el archivo
const withRetry = async <T,>(fn: () => Promise<T>, retries = 3): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 1) throw error;
    return withRetry(fn, retries - 1);
  }
};

export default function ProductsTab() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [priceList, setPriceList] = useState<PriceList[]>([]);
  const [additionalImages, setAdditionalImages] = useState<ProductImage[]>([]);
  const [specifications, setSpecifications] = useState<ProductSpecification[]>([]);
  const mainImageInputRef = useRef<HTMLInputElement>(null);
  const additionalImagesInputRef = useRef<HTMLInputElement>(null);

  // Nuevos estados para búsqueda avanzada
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const [searchField, setSearchField] = useState<'all' | 'name' | 'code' | 'barcode'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedSearch, setAdvancedSearch] = useState({
    name: '',
    code: '',
    barcode: ''
  });
  const debouncedAdvancedSearch = useDebounce(advancedSearch, 400);

  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [selectedProductForBarcode, setSelectedProductForBarcode] = useState<Product | null>(null);

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(20);

  // Permitir selección múltiple de categorías
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Estado para toggle entre vista de tabla y cards
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');

  // Ref para trackear si debemos cargar productos
  const loadProductsRef = useRef(true);

  useEffect(() => {
    // Resetear la página actual al cambiar los filtros
    if (currentPage !== 1) {
      loadProductsRef.current = false; // Evitar doble carga
      setCurrentPage(1);
    } else {
      fetchProducts();
    }
  }, [debouncedSearchTerm, searchField, filterCategory, debouncedAdvancedSearch]);

  // Cargar productos cuando cambie la página
  useEffect(() => {
    if (loadProductsRef.current) {
      fetchProducts();
    } else {
      loadProductsRef.current = true;
    }
  }, [currentPage]);

  // Cargar categorías solo una vez al inicio
  useEffect(() => {
    fetchCategories();
  }, []);

  // Suscribirse a cambios en productos
  useEffect(() => {
    // Realtime subscription removed for egress optimization
    return () => {
      // Subscription cleanup removed for egress optimization
    };
  }, []); // Sin dependencias para evitar múltiples suscripciones

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Capturar el valor actual de la página para esta operación
      const currentPageForFetch = currentPage;

      // Limpiar la caché para asegurar datos actualizados
      clearQueryCache('admin_products');
      
      const fetchProductsData = async () => {
        let query = supabase
          .from('products')
          .select(`
            id, 
            name, 
            description, 
            base_price, 
            main_image, 
            stock, 
            min_order, 
            status, 
            created_at, 
            code, 
            barcode,
            show_in_ecommerce,
            company,
            product_categories:product_categories(category:categories(id, name)),
            prices:product_prices(id, price_type, min_quantity, price, offer_price, offer_days)
          `, { count: 'exact' });
        
        // Aplicar búsqueda por término si existe (usando valor debounced)
        if (debouncedSearchTerm) {
          if (searchField === 'all') {
            // Buscar en todos los campos usando OR
            query = query.or(`name.ilike.%${debouncedSearchTerm}%,code.ilike.%${debouncedSearchTerm}%,barcode.ilike.%${debouncedSearchTerm}%`);
          } else if (searchField === 'name') {
            query = query.ilike('name', `%${debouncedSearchTerm}%`);
          } else if (searchField === 'code') {
            query = query.ilike('code', `%${debouncedSearchTerm}%`);
          } else if (searchField === 'barcode') {
            query = query.ilike('barcode', `%${debouncedSearchTerm}%`);
          }
        }

        // Aplicar filtros avanzados (usando valores debounced)
        if (debouncedAdvancedSearch.name) {
          query = query.ilike('name', `%${debouncedAdvancedSearch.name}%`);
        }
        if (debouncedAdvancedSearch.code) {
          query = query.ilike('code', `%${debouncedAdvancedSearch.code}%`);
        }
        if (debouncedAdvancedSearch.barcode) {
          query = query.ilike('barcode', `%${debouncedAdvancedSearch.barcode}%`);
        }
        
        // Filtrar por categoría
        if (filterCategory) {
          // Filtrar usando la tabla intermedia product_categories
          query = query.eq('product_categories.category_id', filterCategory);
        }
        
        // Ordenar y aplicar paginación
        const startRow = (currentPageForFetch - 1) * itemsPerPage;
        const endRow = currentPageForFetch * itemsPerPage - 1;
        
        query = query.order('created_at', { ascending: false })
                     .range(startRow, endRow);
                     
        // Ejecutar la consulta
        return await query;
      };
      
      // No usar caché para asegurar datos frescos
      const { data, error, count } = await fetchProductsData();

      if (error) throw error;

      // Calcular el total de páginas
      const totalCount = count || 0;
      setTotalCount(totalCount);
      setTotalPages(Math.ceil(totalCount / itemsPerPage));

      // Formatear los datos para que sean compatibles con el tipo Product
      const formattedProducts: Product[] = (data || []).map((item: any) => {
        // Verificar si algún precio tiene oferta
        const hasAnyOffer = Array.isArray(item.prices) && item.prices.some(
          (p: any) => p.offer_price != null && p.offer_price > 0
        );

        return {
          id: item.id,
          name: item.name,
          description: item.description || '',
          base_price: item.base_price || 0,
          main_image: item.main_image || '',
          stock: item.stock || 0,
          min_order: item.min_order || 1,
          status: item.status as 'active' | 'inactive' | 'out_of_stock',
          // Mapear categorías desde la tabla intermedia
          categories: Array.isArray(item.product_categories) 
            ? item.product_categories
                .filter((pc: any) => pc.category)
                .map((pc: any) => ({
                  id: pc.category.id,
                  name: pc.category.name
                }))
            : [],
          created_at: item.created_at,
          code: item.code || undefined,
          barcode: item.barcode || undefined,
          show_in_ecommerce: item.show_in_ecommerce || false,
          company: item.company || [],
          has_offer: hasAnyOffer, // Propiedad a nivel de producto
          prices: (item.prices || []).map((p: any) => ({
            price_type: p.price_type,
            min_quantity: p.min_quantity,
            price: p.price,
            offer_price: p.offer_price ?? null,
            offer_days: p.offer_days ?? null,
            has_offer: p.offer_price != null && p.offer_price > 0
          }))
        };
      });

      setProducts(formattedProducts);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar los productos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    // Clave única para la caché
    const cacheKey = 'admin_product_categories';

    try {
      // Función para obtener las categorías
      const fetchCategoriesData = async () => {
        const { data, error } = await supabase
          .from('categories')
          .select(`
            id,
            name,
            image,
            parent_id,
            parent:categories(id, name)
          `)
          .order('name', { ascending: true });

        if (error) throw error;
        return data || [];
      };

      // Usar caché para las categorías (TTL de 10 minutos)
      const categoriesData = await withCache(cacheKey, fetchCategoriesData, 600);
      // Corregir tipado con casting seguro
      setCategories(categoriesData as unknown as Category[]);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar las categorías');
    }
  };

  const handleImageUpload = async (file: File, isMainImage: boolean = true) => {
    if (!file) return;

    try {
      setUploadingImage(true);
      setError(null);

      const timestamp = new Date().getTime();
      const fileExt = file.name.split('.').pop();
      const fileName = `${timestamp}.${fileExt}`;
      const filePath = `product-images/${fileName}`;

      // Verificar que la imagen no sea demasiado grande
      if (file.size > 5 * 1024 * 1024) {
        throw new Error("La imagen es demasiado grande. El tamaño máximo es 5MB.");
      }

      // Subir la imagen a Supabase Storage - usando el bucket 'public' que ya existe
      const { data, error } = await supabase.storage
        .from('public')
        .upload(filePath, file, {
          cacheControl: '3600', // 1 hora de caché para las imágenes
          upsert: false,
          contentType: file.type // Asegurar que el tipo de contenido sea correcto
        });

      if (error) {
        console.error("Error al subir la imagen a Supabase:", error);
        throw new Error(`Error al subir la imagen: ${error.message}`);
      }

      if (!data) {
        throw new Error("No se recibió respuesta del servidor al subir la imagen");
      }

      // Obtener la URL pública de la imagen
      const { data: urlData } = supabase.storage
        .from('public')
        .getPublicUrl(filePath);

      if (!urlData || !urlData.publicUrl) {
        throw new Error("No se pudo obtener la URL pública de la imagen");
      }

      const imageUrl = urlData.publicUrl;

      if (isMainImage) {
        setPreviewImage(imageUrl);
      } else {
        // Añadir a imágenes adicionales
        setAdditionalImages(prev => [
          ...prev,
          {
            image_url: imageUrl,
            order: prev.length
          }
        ]);
      }

      return imageUrl;
    } catch (err) {
      console.error('Error uploading image:', err);
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido al subir la imagen';
      setError(errorMessage);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddSpecification = () => {
    setSpecifications(prev => [
      ...prev,
      { name: '', value: '', order: prev.length }
    ]);
  };

  const handleUpdateSpecification = (index: number, field: keyof ProductSpecification, value: string) => {
    setSpecifications(prev => prev.map((spec, i) => 
      i === index ? { ...spec, [field]: value } : spec
    ));
  };

  const handleRemoveSpecification = (index: number) => {
    setSpecifications(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddProduct = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      // Validaciones básicas
      const name = formData.get('name') as string;
      const description = formData.get('description') as string;
      const stock = parseInt(formData.get('stock') as string);
      const categoryIds = formData.getAll('category_ids') as string[];

      if (!name || !description || isNaN(stock) || categoryIds.length === 0) {
        throw new Error('Por favor complete todos los campos requeridos');
      }

      if (!previewImage) {
        throw new Error('Por favor suba una imagen principal');
      }

      // Valor fijo para company
      const companyArray = ["Disolgua", "Mundo Fiesta"];

      // Crear el producto
      const productData = {
        name,
        description,
        base_price: 0, // El precio base ya no se usa, se determina por la lista de precios
        main_image: previewImage,
        stock,
        min_order: parseInt(formData.get('min_order') as string) || 1,
        status: (formData.get('status') as string) || 'active',
        code: formData.get('code') as string || null,
        barcode: formData.get('barcode') as string || null,
        meta_keywords: formData.get('meta_keywords') as string || null,
        show_in_ecommerce: true,
        company: companyArray
      };

      // Crear producto
      const { data: product, error } = await supabase
        .from('products')
        .insert(productData)
        .select()
        .single();

      if (error) throw error;

      // Crear relaciones en la tabla product_categories
      if (Array.isArray(categoryIds) && categoryIds.length > 0) {
        // Eliminar duplicados
        const uniqueIds = Array.from(new Set(categoryIds));
        const categoryRelations = uniqueIds.map(categoryId => ({
          product_id: product.id,
          category_id: categoryId
        }));

        const { error: categoryError } = await supabase
          .from('product_categories')
          .upsert(categoryRelations, { onConflict: 'product_id,category_id' });

        if (categoryError) throw categoryError;
      }

      // Procesar precios
      if (priceList.length > 0) {
        const priceData = priceList.map(price => ({
          product_id: product.id,
          price_type: price.price_type,
          min_quantity: price.min_quantity,
          price: price.price,
          offer_price: price.has_offer && price.offer_price ? price.offer_price : null,
          offer_days: price.has_offer && price.offer_days ? price.offer_days : null
        }));

        const { error: priceError } = await supabase
          .from('product_prices')
          .insert(priceData);

        if (priceError) throw priceError;
      }

      // Procesar imágenes adicionales
      if (additionalImages.length > 0) {
        const imageData = additionalImages.map(img => ({
          product_id: product.id,
          image_url: img.image_url,
          order: img.order
        }));

        const { error: imageError } = await supabase
          .from('product_images')
          .insert(imageData);

        if (imageError) throw imageError;
      }

      // Procesar especificaciones
      if (specifications.length > 0) {
        const specsData = specifications.map(spec => ({
          product_id: product.id,
          name: spec.name,
          value: spec.value,
          order: spec.order
        }));

        const { error: specsError } = await supabase
          .from('product_specifications')
          .insert(specsData);

        if (specsError) throw specsError;
      }

      // Mostrar mensaje de éxito
      setSuccess(`Producto "${product.name}" añadido correctamente`);
      
      // Resetear formulario
      handleResetForm();

      // Forzar recarga de la lista
      await fetchProducts();
    } catch (err) {
      console.error('Error al crear producto:', err);
      setError(err instanceof Error ? err.message : 'Error al crear el producto');
    }
  };

  const handleAddPriceLevel = (price_type: 'contado' | 'credito' | 'mayorista' | 'publico') => {
    const min_quantity = price_type === 'publico' ? 1 : 2;
    setPriceList([...priceList, { 
      price_type, 
      min_quantity, 
      price: 0, 
      offer_price: null, 
      offer_days: null, 
      has_offer: false 
    }]);
  };

  const handleUpdatePriceLevel = (index: number, field: keyof PriceList, value: any) => {
    const newPriceList = [...priceList];
    // Validar min_quantity para público u oferta
    if (field === 'min_quantity') {
      if (newPriceList[index].price_type === 'publico') {
        value = 1;
      } else {
        value = Math.max(1, value);
      }
    }
    
    // Manejar lógica del campo has_offer
    if (field === 'has_offer' && value === false) {
      // Si se desmarca la oferta, resetear los valores de oferta a null
      newPriceList[index] = { 
        ...newPriceList[index], 
        [field]: value,
        offer_price: null,
        offer_days: null
      };
    } else {
      // Actualización normal de un campo
      newPriceList[index] = { ...newPriceList[index], [field]: value };
    }
    
    setPriceList(newPriceList);
  };

  const handleRemovePriceLevel = (index: number) => {
    setPriceList(priceList.filter((_, i) => i !== index));
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este producto? Esta acción no se puede deshacer.')) {
      try {
        const { error } = await supabase
          .from('products')
          .delete()
          .eq('id', productId);

        if (error) throw error;

        // Actualizar localmente para efecto inmediato
        setProducts(prev => prev.filter(p => p.id !== productId));
        setSuccess('Producto eliminado correctamente');

        // Forzar recarga de la lista
        await fetchProducts();
      } catch (err) {
        console.error('Error al eliminar producto:', err);
        setError(err instanceof Error ? err.message : 'Error al eliminar el producto');
      }
    }
  };

  const handleToggleProductStatus = async (productId: string, currentStatus: 'active' | 'inactive' | 'out_of_stock') => {
    try {
      // Determinar el nuevo estado
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

      // Actualizar el estado en la base de datos
      const { error } = await supabase
        .from('products')
        .update({ status: newStatus })
        .eq('id', productId);

      if (error) throw error;

      // Actualizar la interfaz localmente para efecto inmediato
      setProducts(prev => prev.map(product => 
        product.id === productId ? { ...product, status: newStatus } : product
      ));
      
      setSuccess(`Estado del producto cambiado a ${newStatus === 'active' ? 'activo' : 'inactivo'}`);

      // Forzar recarga de la lista
      await fetchProducts();
    } catch (err) {
      console.error('Error al cambiar estado del producto:', err);
      setError(err instanceof Error ? err.message : 'Error al cambiar el estado del producto');
    }
  };

  const handleEditClick = async (product: Product) => {
    try {
      setLoading(true);
      
      // Obtener todos los datos del producto y sus relaciones en una sola consulta
      const { data: productData, error } = await supabase
        .from('products')
        .select(`
          *,
          prices:product_prices(*),
          images:product_images(*),
          specs:product_specifications(*),
          product_categories:product_categories(category_id)
        `)
        .eq('id', product.id)
        .single();
      
      if (error) throw error;
      if (!productData) {
        throw new Error('No se pudo obtener la información del producto');
      }
      
      setSelectedProduct(productData as unknown as Product);
      setPreviewImage(productData.main_image);
      // Extraer los IDs de la relación product_categories
      const catIds = Array.isArray(productData.product_categories)
        ? productData.product_categories.map((pc: any) => pc.category_id)
        : [];
      setSelectedCategories(catIds);
      
      // Establecer precios y aplicar oferta previa si existe
      const mappedPrices = (productData.prices || []).map((p: any) => ({
        price_type: p.price_type,
        min_quantity: p.min_quantity,
        price: p.price,
        offer_price: p.offer_price || null,
        offer_days: p.offer_days || null,
        has_offer: !!(p.offer_price && p.offer_price > 0)
      }));
      // Si el producto traía sale_price en la tabla products, lo reflejamos como oferta en público
      if (productData.sale_price != null) {
        const idx = mappedPrices.findIndex((pl: PriceList) => pl.price_type === 'publico');
        if (idx !== -1) {
          // precio de oferta tomado de sale_price
          mappedPrices[idx].offer_price = Number(productData.sale_price);
          // calcular días entre inicio y fin de oferta
          if (productData.sale_start_date && productData.sale_end_date) {
            const start = new Date(productData.sale_start_date);
            const end = new Date(productData.sale_end_date);
            const dias = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
            mappedPrices[idx].offer_days = dias;
          }
        }
      }
      setPriceList(mappedPrices);
      
      // Establecer imágenes adicionales
      setAdditionalImages(productData.images || []);
      
      // Establecer especificaciones
      setSpecifications(productData.specs || []);
      
      setShowAddForm(true);
    } catch (err) {
      console.error('Error fetching product details:', err);
      setError(err instanceof Error ? err.message : 'Error al obtener los detalles del producto');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProduct = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      if (!selectedProduct) return;

      // Obtener el código del producto del formulario
      let productCode = formData.get('code') as string || null;
      
      // Obtener las categorías seleccionadas
      const categoryIds = formData.getAll('category_ids') as string[];

      // Valor fijo para company
      const companyArray = ["Disolgua", "Mundo Fiesta"];

      // Preparar datos actualizados
      const updatedProduct = {
        name: formData.get('name') as string,
        description: formData.get('description') as string,
        base_price: 0,
        main_image: previewImage || selectedProduct.main_image,
        stock: parseInt(formData.get('stock') as string) || 0,
        min_order: parseInt(formData.get('min_order') as string) || 1,
        code: productCode,
        barcode: formData.get('barcode') as string || null,
        meta_keywords: formData.get('meta_keywords') as string || null,
        show_in_ecommerce: true,
        company: companyArray
      };

      // Actualizar el producto
      const { error: updateError } = await supabase
        .from('products')
        .update(updatedProduct)
        .eq('id', selectedProduct.id);

      if (updateError) throw updateError;

      // Actualizar relaciones de categorías
      const { error: deleteCategoriesError } = await supabase
        .from('product_categories')
        .delete()
        .eq('product_id', selectedProduct.id);

      if (deleteCategoriesError) throw deleteCategoriesError;

      // Crear nuevas relaciones de categorías
      if (Array.isArray(categoryIds) && categoryIds.length > 0) {
        const uniqueIds = Array.from(new Set(categoryIds));
        const categoryRelations = uniqueIds.map(categoryId => ({
          product_id: selectedProduct.id,
          category_id: categoryId
        }));

        const { error: categoryError } = await supabase
          .from('product_categories')
          .upsert(categoryRelations, { onConflict: 'product_id,category_id' });

        if (categoryError) throw categoryError;
      }

      // Actualizar precios (eliminar los existentes y agregar los nuevos)
      const { error: deletePricesError } = await supabase
        .from('product_prices')
        .delete()
        .eq('product_id', selectedProduct.id);

      if (deletePricesError) throw deletePricesError;

      if (priceList.length > 0) {
        const priceData = priceList.map(price => ({
          product_id: selectedProduct.id,
          price_type: price.price_type,
          min_quantity: price.min_quantity,
          price: price.price,
          offer_price: price.has_offer && price.offer_price ? price.offer_price : null,
          offer_days: price.has_offer && price.offer_days ? price.offer_days : null
        }));

        const { error: insertPricesError } = await supabase
          .from('product_prices')
          .insert(priceData);

        if (insertPricesError) throw insertPricesError;
      }

      // Actualizar imágenes adicionales (eliminar y agregar)
      const { error: deleteImagesError } = await supabase
        .from('product_images')
        .delete()
        .eq('product_id', selectedProduct.id);

      if (deleteImagesError) throw deleteImagesError;

      if (additionalImages.length > 0) {
        const imageData = additionalImages.map(img => ({
          product_id: selectedProduct.id,
          image_url: img.image_url,
          order: img.order
        }));

        const { error: insertImagesError } = await supabase
          .from('product_images')
          .insert(imageData);

        if (insertImagesError) throw insertImagesError;
      }

      // Actualizar especificaciones
      const { error: deleteSpecsError } = await supabase
        .from('product_specifications')
        .delete()
        .eq('product_id', selectedProduct.id);

      if (deleteSpecsError) throw deleteSpecsError;

      if (specifications.length > 0) {
        const specsData = specifications.map(spec => ({
          product_id: selectedProduct.id,
          name: spec.name,
          value: spec.value,
          order: spec.order
        }));

        const { error: insertSpecsError } = await supabase
          .from('product_specifications')
          .insert(specsData);

        if (insertSpecsError) throw insertSpecsError;
      }

      setSuccess(`Producto "${updatedProduct.name}" actualizado correctamente`);
      
      // Resetear formulario
      handleResetForm();

      // Forzar recarga de la lista
      await fetchProducts();
    } catch (err: any) {
      console.error('Error al actualizar producto:', err);
      // Extraer mensaje del error si existe, o serializar objeto
      const errorMsg = err?.message || (typeof err === 'object' ? JSON.stringify(err) : String(err));
      setError(errorMsg);
    }
  };

  const handleBarcodeClick = (product: Product) => {
    setSelectedProductForBarcode(product);
    setShowBarcodeModal(true);
  };

  const handleGenerateBarcode = (barcode: string) => {
    if (!selectedProductForBarcode) return;

    // Actualizar el código de barras del producto
    supabase
      .from('products')
      .update({ barcode })
      .eq('id', selectedProductForBarcode.id)
      .then(({ error }) => {
        if (error) {
          console.error('Error al actualizar el código de barras:', error);
          setError('Error al actualizar el código de barras');
        } else {
          // Actualizar el producto seleccionado con el nuevo código de barras
          setSelectedProductForBarcode({
            ...selectedProductForBarcode,
            barcode
          });

          // Actualizar la lista de productos
          fetchProducts();
        }
      });
  };

  const handleAdvancedSearchChange = (field: keyof typeof advancedSearch, value: string) => {
    setAdvancedSearch(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchField('all');
    setFilterCategory('');
    setAdvancedSearch({
      name: '',
      code: '',
      barcode: ''
    });
  };

  // Optimizar manejo de paginación con useCallback para evitar recrear la función en cada render
  const handlePageChange = useCallback((page: number) => {
    // Verificar que la página haya cambiado realmente
    if (page === currentPage) return;

    // Validar límites
    const targetPage = Math.max(1, Math.min(page, totalPages || 1));

    // Actualizar estado - el useEffect se encargará de llamar a fetchProducts
    setCurrentPage(targetPage);
    
    // Scroll al inicio de la página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, totalPages]);

  // Agregar subscripción a cambios en productos
  useEffect(() => {
    // Realtime subscription removed for egress optimization
    // Previously: products_changes subscription

    return () => {
      // Subscription cleanup removed for egress optimization
    };
  }, []);

  const handleResetForm = () => {
    setShowAddForm(false);
    setSelectedProduct(null);
    setPreviewImage(null);
    setPriceList([]);
    setSpecifications([]);
    setAdditionalImages([]); 
    setSelectedCategories([]);
    setError(null);
    setSuccess(null);
  };

  // Componente de paginación simplificado
  const Pagination = () => {
    return (
      <div className="flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 bg-white px-3 sm:px-4 py-2 sm:py-3 gap-2">
        {/* Vista móvil */}
        <div className="flex flex-col items-center w-full gap-2 sm:hidden">
          <p className="text-xs text-gray-600 text-center">
            Página <span className="font-bold">{currentPage}</span> de <span className="font-bold">{totalPages}</span>
            <span className="mx-1">•</span>
            <span className="font-medium">{totalCount}</span> productos
          </p>
          <div className="flex justify-center gap-2 w-full">
            <button
              type="button"
              onClick={() => {
                if (currentPage > 1) handlePageChange(currentPage - 1);
              }}
              disabled={currentPage <= 1}
              className={`flex-1 max-w-[120px] inline-flex items-center justify-center rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                currentPage <= 1 
                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' 
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100'
              }`}
            >
              ← Anterior
            </button>
            <button
              type="button"
              onClick={() => {
                if (currentPage < totalPages) handlePageChange(currentPage + 1);
              }}
              disabled={currentPage >= totalPages}
              className={`flex-1 max-w-[120px] inline-flex items-center justify-center rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                currentPage >= totalPages 
                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' 
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100'
              }`}
            >
              Siguiente →
            </button>
          </div>
        </div>
        
        {/* Vista desktop */}
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Mostrando <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}</span> a{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalCount)}</span> de{' '}
              <span className="font-medium">{totalCount}</span> resultados - Página {currentPage} de {totalPages}
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Paginación">
              {/* Primera página */}
              <button
                type="button"
                onClick={() => {
                  if (currentPage > 1) handlePageChange(1);
                }}
                disabled={currentPage <= 1}
                className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                  currentPage <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Primera página</span>
                <ChevronFirst className="h-5 w-5" />
              </button>
              
              {/* Anterior */}
              <button
                type="button"
                onClick={() => {
                  if (currentPage > 1) handlePageChange(currentPage - 1);
                }}
                disabled={currentPage <= 1}
                className={`relative inline-flex items-center px-2 py-2 ${
                  currentPage <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Anterior</span>
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              {/* Indicador de página */}
              <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold bg-blue-50 border border-blue-500 text-blue-600 min-w-[80px] justify-center">
                {currentPage} de {totalPages}
              </span>
              
              {/* Siguiente */}
              <button
                type="button"
                onClick={() => {
                  if (currentPage < totalPages) handlePageChange(currentPage + 1);
                }}
                disabled={currentPage >= totalPages}
                className={`relative inline-flex items-center px-2 py-2 ${
                  currentPage >= totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Siguiente</span>
                <ChevronRight className="h-5 w-5" />
              </button>
              
              {/* Última página */}
              <button
                type="button"
                onClick={() => {
                  if (currentPage < totalPages) handlePageChange(totalPages);
                }}
                disabled={currentPage >= totalPages}
                className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                  currentPage >= totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Última página</span>
                <ChevronLast className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  // Mostrar la paginación en el markup de productos
  // Calcular estadísticas
  const stats = useMemo(() => {
    const lowStockCount = products.filter(p => p.stock < 10).length;
    const outOfStockCount = products.filter(p => p.stock === 0).length;
    const categoryCount = new Set(products.flatMap(p => p.categories?.map(c => c.id) || [])).size;
    const activeCount = products.filter(p => p.status === 'active').length;

    return {
      total: totalCount,
      lowStock: lowStockCount,
      outOfStock: outOfStockCount,
      categories: categoryCount,
      active: activeCount
    };
  }, [products, totalCount]);

  return (
    <div className="space-y-3 sm:space-y-4 w-full max-w-full overflow-x-hidden px-2 sm:px-0">
      {!showAddForm ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-4 w-full max-w-full">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-2.5 sm:p-3 md:p-4 text-white w-full min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className="text-[10px] sm:text-xs md:text-sm font-medium opacity-90 truncate">Total Productos</p>
                  <p className="text-lg sm:text-xl md:text-2xl font-bold mt-0.5 sm:mt-1 truncate">{stats.total}</p>
                </div>
                <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-2.5 sm:p-3 md:p-4 text-white w-full min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className="text-[10px] sm:text-xs md:text-sm font-medium opacity-90 truncate">Activos</p>
                  <p className="text-lg sm:text-xl md:text-2xl font-bold mt-0.5 sm:mt-1 truncate">{stats.active}</p>
                </div>
                <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                  <Power className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg shadow-lg p-2.5 sm:p-3 md:p-4 text-white w-full min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className="text-[10px] sm:text-xs md:text-sm font-medium opacity-90 truncate">Stock Bajo</p>
                  <p className="text-lg sm:text-xl md:text-2xl font-bold mt-0.5 sm:mt-1 truncate">{stats.lowStock}</p>
                </div>
                <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-2.5 sm:p-3 md:p-4 text-white w-full min-w-0 lg:col-span-1 sm:col-span-3 xs:col-span-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className="text-[10px] sm:text-xs md:text-sm font-medium opacity-90 truncate">Categorías</p>
                  <p className="text-lg sm:text-xl md:text-2xl font-bold mt-0.5 sm:mt-1 truncate">{stats.categories}</p>
                </div>
                <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                  <Tag className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white shadow-lg rounded-xl p-2 sm:p-3 md:p-4 overflow-hidden w-full">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 mb-2 sm:mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">
                  Productos
                  <span className="ml-1 sm:ml-2 inline-flex items-center justify-center px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-bold rounded-full bg-blue-600 text-white shadow-sm">
                    {totalCount}
                  </span>
                </h1>
              </div>
              <div className="flex gap-1.5 flex-row flex-shrink-0">
                {/* Toggle de vista */}
                <div className="hidden sm:flex border-2 border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 transition-colors ${
                      viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    title="Vista de tarjetas"
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`p-2 transition-colors ${
                      viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    title="Vista de tabla"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold shadow-md hover:shadow-lg transition-all whitespace-nowrap"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Nuevo Producto</span>
                  <span className="sm:hidden">Nuevo</span>
                </button>
              </div>
            </div>

            {/* Barra de búsqueda mejorada */}
            <div className="mb-3 sm:mb-4 w-full">
              <div className="flex gap-1.5 sm:gap-2 mb-2">
                <div className="relative flex-1 min-w-0">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 sm:pl-10 pr-8 sm:pr-10 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <select
                  value={searchField}
                  onChange={(e) => setSearchField(e.target.value as 'all' | 'name' | 'code' | 'barcode')}
                  className="px-2 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0 min-w-[70px]"
                >
                  <option value="all">🔍</option>
                  <option value="name">📝</option>
                  <option value="code">🏷️</option>
                  <option value="barcode">📊</option>
                </select>
              </div>
            

              <div className="flex items-center justify-between text-xs mt-1.5">
                <button
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  {showAdvancedSearch ? 'Ocultar filtros' : 'Filtros avanzados'}
                  <SlidersHorizontal className="w-3 h-3" />
                </button>

                {(searchTerm || filterCategory || advancedSearch.name || advancedSearch.code || advancedSearch.barcode) && (
                  <button
                    onClick={clearFilters}
                    className="text-gray-600 hover:text-gray-800 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Limpiar
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Panel de búsqueda avanzada */}
            {showAdvancedSearch && (
              <div className="mb-3 bg-gradient-to-br from-gray-50 to-gray-100 p-3 sm:p-4 rounded-lg border border-gray-200">
                <p className="text-[10px] text-gray-500 mb-2 font-medium">FILTROS AVANZADOS</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                  <div>
                    <label className="block text-[10px] sm:text-xs text-gray-600 mb-1.5 font-medium flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                      Nombre del producto
                    </label>
                    <input
                      type="text"
                      value={advancedSearch.name}
                      onChange={(e) => handleAdvancedSearchChange('name', e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      placeholder="Ej: Vaso plástico..."
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] sm:text-xs text-gray-600 mb-1.5 font-medium flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      Código interno
                    </label>
                    <input
                      type="text"
                      value={advancedSearch.code}
                      onChange={(e) => handleAdvancedSearchChange('code', e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      placeholder="Ej: PB001..."
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] sm:text-xs text-gray-600 mb-1.5 font-medium flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                      Código de barras
                    </label>
                    <input
                      type="text"
                      value={advancedSearch.barcode}
                      onChange={(e) => handleAdvancedSearchChange('barcode', e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      placeholder="Ej: 7501234567890..."
                    />
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3 sm:p-4 text-red-700 shadow-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium break-words">{error}</p>
                  </div>
                  <button 
                    onClick={() => setError(null)}
                    className="text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
                    aria-label="Cerrar mensaje"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-3 sm:p-4 text-green-700 shadow-sm">
                <div className="flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium break-words">{success}</p>
                  </div>
                  <button 
                    onClick={() => setSuccess(null)}
                    className="text-green-500 hover:text-green-700 transition-colors flex-shrink-0"
                    aria-label="Cerrar mensaje"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Products List */}
            {!loading && products.length > 0 && (
              <div className="bg-white shadow rounded-lg overflow-hidden w-full">
                {/* Vista de Cards/Grid - Optimizado */}
                {viewMode === 'grid' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1.5 sm:gap-2 md:gap-3 p-1.5 sm:p-2 md:p-3 w-full">
                    {products.map((product) => (
                      <div key={product.id} className="group bg-white border border-gray-200 rounded-md sm:rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 w-full max-w-full">
                        {/* Imagen del producto - Reducida en móvil */}
                        <div className="relative bg-gray-50 overflow-hidden h-24 sm:h-32 md:h-40">
                          <ProxyImage
                            src={product.main_image}
                            alt={product.name}
                            className="w-full h-full object-contain p-1 sm:p-2 group-hover:scale-105 transition-transform duration-300"
                          />
                          {/* Badges - Solo en tablet+ */}
                          <div className="hidden sm:flex absolute top-1 left-1 flex-col gap-0.5">
                            {product.prices?.some(p => p.has_offer) && (
                              <span className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 px-1 py-0.5 rounded-full text-[10px] font-bold shadow-sm">
                                OFERTA
                              </span>
                            )}
                            <span className={`inline-flex items-center px-1 py-0.5 rounded-full text-[10px] font-semibold shadow-sm ${
                              product.status === 'active'
                                ? 'bg-green-500 text-white'
                                : product.status === 'out_of_stock'
                                ? 'bg-red-500 text-white'
                                : 'bg-gray-500 text-white'
                            }`}>
                              {product.status === 'active' ? 'Activo' : product.status === 'out_of_stock' ? 'Sin Stock' : 'Inactivo'}
                            </span>
                          </div>
                        </div>

                        {/* Contenido de la card */}
                        <div className="p-2 sm:p-2.5 md:p-3">
                          {/* Nombre del producto */}
                          <h3 className="font-bold text-gray-900 text-xs sm:text-sm md:text-base line-clamp-2 mb-1 sm:mb-1.5 leading-snug">
                            {product.name}
                          </h3>

                          {/* Stock y estado - Compacto en móvil */}
                          <div className="flex items-center justify-between mb-1 sm:mb-1.5">
                            <span className={`text-xs sm:text-sm font-extrabold ${product.stock < 10 ? 'text-red-600' : 'text-green-600'}`}>
                              Stock: {product.stock}
                            </span>
                            {product.prices?.some(p => p.has_offer) && (
                              <span className="sm:hidden inline-flex items-center bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded-full text-[9px] font-bold">
                                OFERTA
                              </span>
                            )}
                          </div>

                          {/* Precios - Mejorados */}
                          <div className="mb-1 sm:mb-1.5">
                            {product.prices && product.prices.length > 0 ? (
                              <div className="grid grid-cols-2 gap-1">
                                {/* Mostrar solo los 2 primeros precios en móvil */}
                                {product.prices.slice(0, 2).map((price) => {
                                  const colors = {
                                    publico: 'bg-red-100 border-red-300 text-red-800',
                                    contado: 'bg-green-100 border-green-300 text-green-800',
                                    credito: 'bg-blue-100 border-blue-300 text-blue-800',
                                    mayorista: 'bg-purple-100 border-purple-300 text-purple-800'
                                  };
                                  const labels = {
                                    publico: 'Púb.',
                                    contado: 'Cont.',
                                    credito: 'Créd.',
                                    mayorista: 'May.'
                                  };
                                  return (
                                    <div key={price.price_type} className={`${colors[price.price_type]} border rounded-md px-1 py-1`}>
                                      <div className="text-[9px] sm:text-[10px] font-bold text-center">{labels[price.price_type]}</div>
                                      <div className="text-xs sm:text-sm font-extrabold text-center truncate">
                                        Q{price.price.toFixed(2)}
                                      </div>
                                    </div>
                                  );
                                })}
                                {/* Indicador de más precios */}
                                {product.prices.length > 2 && (
                                  <div className="col-span-2 text-center text-[10px] text-gray-500 mt-0.5 font-medium">
                                    +{product.prices.length - 2} más
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center text-[10px] sm:text-xs text-gray-500 py-1 bg-gray-100 rounded font-medium">
                                Sin precios
                              </div>
                            )}
                          </div>

                          {/* Acciones - Mejoradas */}
                          <div className="flex items-center gap-1 pt-1 sm:pt-1.5 border-t border-gray-200">
                            <button
                              onClick={() => handleEditClick(product)}
                              className="flex-1 flex items-center justify-center px-1 py-1 sm:py-1.5 bg-blue-600 text-white rounded-md text-[10px] sm:text-xs font-bold hover:bg-blue-700 transition-colors shadow-sm"
                              title="Editar"
                            >
                              <Edit className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            </button>
                            <button
                              onClick={() => handleToggleProductStatus(product.id, product.status)}
                              className={`p-1 sm:p-1.5 rounded-md transition-colors shadow-sm ${
                                product.status === 'active'
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title={product.status === 'active' ? 'Desactivar' : 'Activar'}
                            >
                              <Power className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.id)}
                              className="p-1 sm:p-1.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors shadow-sm"
                              title="Eliminar"
                            >
                              <Trash className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Vista de Tabla - Optimizada */}
                {viewMode === 'table' && (
                  <>
                    {/* Vista móvil y tablet */}
                    <div className="block lg:hidden">
                  <div className="divide-y divide-gray-200 prevent-overflow">
                    {products.map((product) => (
                      <div key={product.id} className="p-3 sm:p-4 hover:bg-gray-50 border-b border-gray-200">
                        <div className="flex items-start gap-3">
                          <div className="relative w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                            <ProxyImage
                              src={product.main_image}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex justify-between items-center gap-2 mb-1.5">
                              <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate max-w-[60%]">
                                {product.name}
                              </h3>
                              {product.prices?.some(p => p.has_offer) && (
                                <span className="inline-flex items-center bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap shadow-sm">
                                  Oferta
                                </span>
                              )}
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm
                                ${product.status === 'active'
                                  ? 'bg-green-500 text-white'
                                  : product.status === 'out_of_stock'
                                    ? 'bg-red-500 text-white'
                                    : 'bg-gray-500 text-white'
                                }`}
                              >
                                {product.status === 'active'
                                  ? 'Activo'
                                  : product.status === 'out_of_stock'
                                    ? 'Sin Stock'
                                    : 'Inactivo'}
                              </span>
                            </div>

                            <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
                              {product.prices && product.prices.length > 0 ? (
                                <>
                                  {product.prices.some(p => p.price_type === 'publico') && (
                                    <span className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded-lg font-bold whitespace-nowrap border border-red-300">
                                      Q{product.prices.find(p => p.price_type === 'publico')?.price.toFixed(2)}
                                    </span>
                                  )}
                                  {product.prices.some(p => p.price_type === 'contado') && (
                                    <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded-lg font-bold whitespace-nowrap border border-green-300">
                                      Q{product.prices.find(p => p.price_type === 'contado')?.price.toFixed(2)}
                                    </span>
                                  )}
                                  {product.prices.some(p => p.price_type === 'credito') && (
                                    <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-lg font-bold whitespace-nowrap border border-blue-300">
                                      Q{product.prices.find(p => p.price_type === 'credito')?.price.toFixed(2)}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="text-gray-500 font-medium">Sin precios</span>
                              )}
                            </div>

                            <div className="mt-2 text-xs sm:text-sm">
                              <div className="flex flex-col gap-1">
                                <div className="flex justify-between">
                                  <div className="truncate">
                                    <span className="text-gray-600 font-medium">Código:</span> <span className="font-bold text-gray-900">{product.code || '-'}</span>
                                  </div>
                                  <div className="truncate">
                                    <span className="text-gray-600 font-medium">Stock:</span> <span className="font-extrabold text-gray-900">{product.stock}</span>
                                  </div>
                                </div>
                                <div className="truncate">
                                  <span className="text-gray-600 font-medium">Categoría:</span> <span className="font-bold text-gray-900">{product.categories?.map(category => category.name).join(', ') || '-'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Acciones del producto en móvil */}
                            <div className="mt-2 flex justify-end items-center">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEditClick(product)}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  aria-label="Editar producto"
                                >
                                  <Edit className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleToggleProductStatus(product.id, product.status)}
                                  className={`p-1 ${product.status === 'active' ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-gray-600'}`}
                                  aria-label={product.status === 'active' ? 'Desactivar producto' : 'Activar producto'}
                                >
                                  <Power className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleBarcodeClick(product)}
                                  className="text-purple-600 hover:text-purple-800 p-1"
                                  aria-label="Código de barras"
                                >
                                  <BarcodeIcon className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Vista desktop - Lista Mejorada */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10 border-b-2 border-gray-300">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                          Producto
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                          Códigos
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                          Stock
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                          Precios
                        </th>
                        <th scope="col" className="px-4 py-3 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">
                          Estado
                        </th>
                        <th scope="col" className="px-4 py-3 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map((product) => (
                        <tr key={product.id} className="hover:bg-blue-50/50 transition-colors group">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="relative w-14 h-14 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0 border-2 border-gray-200 group-hover:border-blue-400 transition-colors">
                                <ProxyImage
                                  src={product.main_image}
                                  alt={product.name}
                                  className="w-full h-full object-contain p-1"
                                />
                                {product.prices?.some(p => p.has_offer) && (
                                  <div className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-md">
                                    OFERTA
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-bold text-gray-900 text-base line-clamp-1 group-hover:text-blue-600 transition-colors">
                                  {product.name}
                                </h3>
                                {product.categories && product.categories.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {product.categories.slice(0, 2).map((cat, idx) => (
                                      <span key={idx} className="inline-flex items-center bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-bold border border-blue-300">
                                        {cat.name}
                                      </span>
                                    ))}
                                    {product.categories.length > 2 && (
                                      <span className="text-xs text-gray-600 font-medium">+{product.categories.length - 2}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              {product.code && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-gray-600 font-medium">Cód:</span>
                                  <span className="text-sm font-mono font-bold text-gray-900">{product.code}</span>
                                </div>
                              )}
                              {product.barcode && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-gray-600 font-medium">Bar:</span>
                                  <span className="text-sm font-mono font-bold text-gray-800">{product.barcode}</span>
                                </div>
                              )}
                              {!product.code && !product.barcode && (
                                <span className="text-sm text-gray-400 font-medium">-</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-2.5 h-2.5 rounded-full ${product.stock < 10 ? 'bg-red-500' : product.stock < 50 ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
                              <span className={`text-base font-extrabold ${product.stock < 10 ? 'text-red-600' : product.stock < 50 ? 'text-yellow-600' : 'text-green-600'}`}>
                                {product.stock}
                              </span>
                              <span className="text-xs text-gray-500 font-medium">uds</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {product.prices && product.prices.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {product.prices.some(p => p.price_type === 'publico') && (
                                  <span className="inline-flex items-center bg-red-100 border border-red-300 text-red-800 px-2 py-1 rounded-md text-xs font-bold">
                                    P: Q{product.prices.find(p => p.price_type === 'publico')?.price.toFixed(2)}
                                  </span>
                                )}
                                {product.prices.some(p => p.price_type === 'contado') && (
                                  <span className="inline-flex items-center bg-green-100 border border-green-300 text-green-800 px-2 py-1 rounded-md text-xs font-bold">
                                    C: Q{product.prices.find(p => p.price_type === 'contado')?.price.toFixed(2)}
                                  </span>
                                )}
                                {product.prices.some(p => p.price_type === 'credito') && (
                                  <span className="inline-flex items-center bg-blue-100 border border-blue-300 text-blue-800 px-2 py-1 rounded-md text-xs font-bold">
                                    Cr: Q{product.prices.find(p => p.price_type === 'credito')?.price.toFixed(2)}
                                  </span>
                                )}
                                {product.prices.some(p => p.price_type === 'mayorista') && (
                                  <span className="inline-flex items-center bg-purple-100 border border-purple-300 text-purple-800 px-2 py-1 rounded-md text-xs font-bold">
                                    M: Q{product.prices.find(p => p.price_type === 'mayorista')?.price.toFixed(2)}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400 font-medium">Sin precios</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-extrabold shadow-sm ${
                              product.status === 'active'
                                ? 'bg-green-500 text-white'
                                : product.status === 'out_of_stock'
                                ? 'bg-red-500 text-white'
                                : 'bg-gray-500 text-white'
                            }`}>
                              {product.status === 'active' ? 'Activo' : product.status === 'out_of_stock' ? 'Sin Stock' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => handleEditClick(product)}
                                className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors shadow-sm"
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleToggleProductStatus(product.id, product.status)}
                                className={`p-2 rounded-lg transition-colors shadow-sm ${
                                  product.status === 'active'
                                    ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                                title={product.status === 'active' ? 'Desactivar' : 'Activar'}
                              >
                                <Power className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleBarcodeClick(product)}
                                className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors shadow-sm"
                                title="Barcode"
                              >
                                <BarcodeIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product.id)}
                                className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors shadow-sm"
                                title="Eliminar"
                              >
                                <Trash className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                  </>
                )}

                {/* Añadir paginación solo si hay más de una página */}
                {totalPages > 1 && (
                  <div className="border-t border-gray-200 px-4 py-3">
                    <Pagination />
                  </div>
                )}
              </div>
            )}

            {/* No products message */}
            {!loading && products.length === 0 && (
              <div className="bg-white shadow rounded-lg p-6 sm:p-8 text-center">
                <div className="flex flex-col items-center justify-center gap-3 sm:gap-4">
                  <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-3 sm:p-4 rounded-full">
                    <Search className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">No se encontraron productos</h3>
                  <p className="text-xs sm:text-sm text-gray-500 max-w-md px-2">
                    {searchTerm || filterCategory ?
                      'No hay productos que coincidan con los criterios de búsqueda. Intenta con otros términos o limpia los filtros.' :
                      'Aún no hay productos en el sistema. Haz clic en "Nuevo Producto" para añadir uno.'}
                  </p>
                  {(searchTerm || filterCategory) && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilterCategory('');
                      }}
                      className="mt-2 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm text-sm font-medium"
                    >
                      <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
                      Limpiar filtros
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Loading indicator */}
            {loading && (
              <div className="bg-white shadow rounded-lg p-6 sm:p-8 text-center">
                <div className="flex flex-col items-center justify-center gap-3 sm:gap-4">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
                    <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-blue-400 absolute top-0 left-0" style={{ animationDirection: 'reverse' }}></div>
                  </div>
                  <div>
                    <p className="text-sm sm:text-base text-gray-700 font-medium">Cargando productos...</p>
                    <p className="text-xs text-gray-400 mt-1">Por favor espera un momento</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Add/Edit Product Form */
        <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-semibold truncate">
                {selectedProduct ? 'Editar Producto' : 'Añadir Nuevo Producto'}
              </h2>
            </div>

            <div className="flex gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={handleResetForm}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 text-sm w-full sm:w-auto"
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="product-form"
                disabled={uploadingImage || !previewImage}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-1.5 w-full sm:w-auto"
              >
                {uploadingImage ? (
                  <>
                    <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Procesando...</span>
                  </>
                ) : (
                  <span>{selectedProduct ? 'Guardar cambios' : 'Crear producto'}</span>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 mb-4 text-sm">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-600 flex justify-between items-center mb-4 text-sm">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {success}
              </div>
              <button 
                onClick={() => setSuccess(null)}
                className="text-green-600 hover:text-green-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <form id="product-form" onSubmit={selectedProduct ? handleEditProduct : handleAddProduct} className="space-y-3">
           

            {/* Sección 1: Información Básica */}
            <div id="informacion-basica" className="p-3 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium mb-3">Información Básica</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nombre del Producto*
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    defaultValue={selectedProduct?.name}
                    className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Ej: Vaso plástico 16oz"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Categoría*
                  </label>
                  {/* Nuevo selector con react-select */}
                  <Select
                    isMulti
                    name="category_ids"
                    options={[
                      // Agrupar por padre/hijo
                      ...categories
                        .filter(category => !category.parent_id)
                        .map(parentCategory => ({
                          value: parentCategory.id,
                          label: parentCategory.name,
                          isParent: true as const
                        })),
                      ...categories
                        .filter(category => category.parent_id)
                        .map(childCategory => ({
                          value: childCategory.id,
                          label: `— ${childCategory.name}${childCategory.parent ? ` (${childCategory.parent.name})` : ''}`,
                          isParent: false as const
                        }))
                    ]}
                    classNamePrefix="react-select"
                    value={categories.filter(cat => selectedCategories.includes(cat.id)).map(cat => ({ value: cat.id, label: cat.name, isParent: !!cat.parent_id }))}
                    onChange={(selected) => {
                      setSelectedCategories(selected ? selected.map((opt: any) => opt.value) : []);
                    }}
                    placeholder="Selecciona una o varias categorías"
                    closeMenuOnSelect={false}
                    styles={{
                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                      option: (provided, state) => ({
                        ...provided,
                        fontWeight: state.data.isParent ? 'bold' : 'normal',
                        paddingLeft: state.data.isParent ? 12 : 28
                      })
                    }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Código del Producto
                  </label>
                  <input
                    type="text"
                    name="code"
                    defaultValue={selectedProduct?.code || ''}
                    className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Ej: VP-16"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Código de Barras
                  </label>
                  <input
                    type="text"
                    name="barcode"
                    defaultValue={selectedProduct?.barcode || ''}
                    className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Ej: 7501234567890"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Stock*
                  </label>
                  <input
                    type="number"
                    name="stock"
                    required
                    min="0"
                    defaultValue={selectedProduct?.stock}
                    className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Unidades disponibles"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Palabras Clave (SEO)
                  </label>
                  <input
                    type="text"
                    name="meta_keywords"
                    defaultValue={selectedProduct?.meta_keywords || ''}
                    placeholder="palabra1, palabra2, palabra3"
                    className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Descripción*
                  </label>
                  <textarea
                    name="description"
                    required
                    rows={2}
                    defaultValue={selectedProduct?.description}
                    className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Descripción detallada del producto..."
                  ></textarea>
                </div>

                {/* Campos ocultos pero necesarios */}
                <input
                  type="hidden"
                  id="show_in_ecommerce"
                  name="show_in_ecommerce"
                  value="true"
                />
              </div>
            </div>

            {/* Sección 2: Imágenes */}
            <div id="imagenes" className="p-3 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium mb-3">Imágenes del Producto</h3>

              <div className="space-y-3">
                {/* Imagen principal */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Imagen principal* <span className="text-[10px] text-gray-500 font-normal">(Obligatoria)</span>
                  </label>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white overflow-hidden">
                    {previewImage ? (
                      <div className="relative p-2 sm:p-3">
                        <div className="w-full h-32 sm:h-40 overflow-hidden rounded-lg bg-gray-50">
                          <ProxyImage
                            src={previewImage}
                            alt="Vista previa de la imagen"
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setPreviewImage(null)}
                          className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-red-500 text-white rounded-full p-1.5 sm:p-2 shadow-lg hover:bg-red-600 transition-colors"
                          aria-label="Eliminar imagen"
                        >
                          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => mainImageInputRef.current?.click()}
                        className="flex flex-col items-center justify-center h-32 sm:h-40 cursor-pointer hover:bg-gray-50 transition-colors p-4"
                      >
                        <Upload className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400 mb-2" />
                        <p className="text-xs sm:text-sm text-gray-500 font-medium">Toca para subir imagen</p>
                        <p className="text-[10px] text-gray-400 mt-1">JPG, PNG o WEBP (máx. 5MB)</p>
                      </div>
                    )}
                    <input
                      ref={mainImageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const imageUrl = await handleImageUpload(file, true);
                          if (imageUrl) {
                            setPreviewImage(imageUrl);
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Imágenes adicionales */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-gray-700">
                      Imágenes adicionales
                    </label>
                    <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {additionalImages.length}/5
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                    {additionalImages.map((img, index) => (
                      <div key={index} className="relative bg-white border-2 border-gray-200 rounded-lg overflow-hidden aspect-square group">
                        <ProxyImage
                          src={img.image_url}
                          alt={`Imagen adicional ${index + 1}`}
                          className="w-full h-full object-contain p-1"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = [...additionalImages];
                              newImages.splice(index, 1);
                              setAdditionalImages(newImages);
                            }}
                            className="bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors shadow-lg"
                            aria-label="Eliminar imagen"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {additionalImages.length < 5 && (
                      <div
                        onClick={() => additionalImagesInputRef.current?.click()}
                        className="bg-white border-2 border-dashed border-gray-300 rounded-lg aspect-square flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 hover:border-gray-400 transition-all"
                      >
                        <Upload className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400 mb-1" />
                        <span className="text-[10px] text-gray-500 font-medium">Añadir</span>
                        <input
                          ref={additionalImagesInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const imageUrl = await handleImageUpload(file, false);
                              if (imageUrl) {
                                setAdditionalImages([
                                  ...additionalImages,
                                  { image_url: imageUrl, order: additionalImages.length }
                                ]);
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Sección 3: Precios */}
            <div id="precios" className="p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                <h3 className="text-sm font-medium">Lista de Precios</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                  <button
                    type="button"
                    onClick={() => handleAddPriceLevel('publico')}
                    className="flex items-center justify-center gap-0.5 px-2 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200"
                  >
                    <Plus className="w-3 h-3" />
                    <span className="truncate">Público</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddPriceLevel('contado')}
                    className="flex items-center justify-center gap-0.5 px-2 py-1 text-xs bg-green-100 text-green-600 rounded hover:bg-green-200"
                  >
                    <Plus className="w-3 h-3" />
                    <span className="truncate">Contado</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddPriceLevel('credito')}
                    className="flex items-center justify-center gap-0.5 px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                  >
                    <Plus className="w-3 h-3" />
                    <span className="truncate">Crédito</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddPriceLevel('mayorista')}
                    className="flex items-center justify-center gap-0.5 px-2 py-1 text-xs bg-purple-100 text-purple-600 rounded hover:bg-purple-200"
                  >
                    <Plus className="w-3 h-3" />
                    <span className="truncate">Mayor</span>
                  </button>
                </div>
              </div>

              {priceList.length === 0 ? (
                <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                  <p className="text-xs text-gray-500">No has añadido ningún precio todavía</p>
                  <p className="text-[10px] text-gray-400 mt-1">Haz clic en los botones de arriba para añadir precios</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {priceList.map((price, index) => (
                    <div key={index} className="bg-white p-3 rounded-lg border border-gray-200 space-y-2">
                      {/* Header con tipo y botón eliminar */}
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                          ${price.price_type === 'contado' ? 'bg-green-100 text-green-800'
                           : price.price_type === 'credito' ? 'bg-blue-100 text-blue-800'
                           : price.price_type === 'mayorista' ? 'bg-purple-100 text-purple-800'
                           : 'bg-red-100 text-red-800'}
                        `}>
                          {price.price_type === 'contado' ? 'Contado'
                            : price.price_type === 'credito' ? 'Crédito'
                            : price.price_type === 'mayorista' ? 'Mayorista'
                            : 'Público'}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemovePriceLevel(index)}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          aria-label="Eliminar precio"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Campos principales en grid */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-600 mb-1 font-medium">
                            Cantidad Mínima
                          </label>
                          {price.price_type === 'publico' ? (
                            <input
                              type="number"
                              value={1}
                              disabled
                              className="w-full px-2 py-1.5 border rounded-lg bg-gray-100 text-gray-700 text-xs"
                            />
                          ) : (
                            <input
                              type="number"
                              min="1"
                              value={price.min_quantity}
                              onChange={(e) => handleUpdatePriceLevel(index, 'min_quantity', parseInt(e.target.value))}
                              className="w-full px-2 py-1.5 border rounded-lg focus:ring-1 focus:ring-blue-500 text-xs"
                            />
                          )}
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-600 mb-1 font-medium">Precio (Q)</label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">Q</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={price.price}
                              onChange={(e) => handleUpdatePriceLevel(index, 'price', parseFloat(e.target.value))}
                              className="w-full pl-6 pr-2 py-1.5 border rounded-lg focus:ring-1 focus:ring-blue-500 text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sección de oferta */}
                      <div className="pt-2 border-t border-gray-100">
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            id={`has-offer-${index}`}
                            checked={price.has_offer || false}
                            onChange={(e) => handleUpdatePriceLevel(index, 'has_offer', e.target.checked)}
                            className="h-3.5 w-3.5 text-blue-600 rounded mr-2"
                          />
                          <label htmlFor={`has-offer-${index}`} className="text-xs text-gray-700 font-medium">
                            Aplicar oferta especial
                          </label>
                        </div>
                        {price.has_offer && (
                          <div className="grid grid-cols-2 gap-2 pl-5">
                            <div>
                              <label className="block text-[10px] text-gray-600 mb-1 font-medium">Precio Oferta (Q)</label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">Q</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={price.offer_price || 0}
                                  onChange={e => handleUpdatePriceLevel(index, 'offer_price', parseFloat(e.target.value))}
                                  className="w-full pl-6 pr-2 py-1.5 border rounded-lg focus:ring-1 focus:ring-blue-500 text-xs"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-600 mb-1 font-medium">Días vigencia</label>
                              <input
                                type="number"
                                min="0"
                                value={price.offer_days || 0}
                                onChange={e => handleUpdatePriceLevel(index, 'offer_days', parseInt(e.target.value))}
                                className="w-full px-2 py-1.5 border rounded-lg focus:ring-1 focus:ring-blue-500 text-xs"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sección 4: Especificaciones */}
            <div id="especificaciones" className="p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
                <h3 className="text-sm font-medium">Especificaciones del Producto</h3>
                <button
                  type="button"
                  onClick={handleAddSpecification}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium w-full sm:w-auto justify-center"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Añadir Especificación
                </button>
              </div>

              {specifications.length === 0 ? (
                <div className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200">
                  <div className="flex flex-col items-center gap-2">
                    <div className="bg-gray-100 p-3 rounded-full">
                      <SlidersHorizontal className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 font-medium">No hay especificaciones</p>
                    <p className="text-[10px] sm:text-xs text-gray-400 max-w-xs">
                      Añade características técnicas como material, dimensiones, peso, etc.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {specifications.map((spec, index) => (
                    <div key={index} className="bg-white p-3 rounded-lg border border-gray-200 space-y-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-500 font-medium">
                          Especificación #{index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSpecification(index)}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          aria-label="Eliminar especificación"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-600 mb-1 font-medium">
                            Característica
                          </label>
                          <input
                            type="text"
                            value={spec.name}
                            onChange={(e) => handleUpdateSpecification(index, 'name', e.target.value)}
                            placeholder="Ej: Material, Tamaño..."
                            className="w-full px-2 py-1.5 border rounded-lg focus:ring-1 focus:ring-blue-500 text-xs"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-600 mb-1 font-medium">
                            Valor
                          </label>
                          <input
                            type="text"
                            value={spec.value}
                            onChange={(e) => handleUpdateSpecification(index, 'value', e.target.value)}
                            placeholder="Ej: Plástico, 16oz..."
                            className="w-full px-2 py-1.5 border rounded-lg focus:ring-1 focus:ring-blue-500 text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Modal de Código de Barras */}
      {showBarcodeModal && selectedProductForBarcode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-start gap-2 p-3 sm:p-4 border-b border-gray-200">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                  Código de Barras
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                  {selectedProductForBarcode.name}
                </p>
              </div>
              <button
                onClick={() => setShowBarcodeModal(false)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Cerrar modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4">
              <BarcodeGenerator
                value={selectedProductForBarcode.barcode || ''}
                format="CODE128"
                height={80}
                width={1.8}
                fontSize={14}
                displayValue={true}
                margin={8}
                background="#ffffff"
                lineColor="#000000"
                onBarcodeGenerated={handleGenerateBarcode}
                canGenerate={!selectedProductForBarcode.barcode}
                productName={selectedProductForBarcode.name}
                publicPrice={selectedProductForBarcode.prices?.find(p => p.price_type === 'publico')?.price}
              />
            </div>

            {/* Footer */}
            <div className="flex gap-2 p-3 sm:p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowBarcodeModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}